<template>
  <v-card id="summary-totals" class="px-4 pt-2 pb-4 mb-0 mt-4 elevation-0">
    <v-card-title class="px-1 mb-1">
      <h1 class="card-title mb-1">
        {{ $t('views.dashboardDetail.summary.totals.title') }}
      </h1>
    </v-card-title>
    <v-card-text class="mt-0 pa-0">
      <v-chip-group v-model="yearSelected" active-class="primary" mandatory>
        <v-chip v-for="year in yearsToSelect" :key="year" filter outlined :value="year">
          {{ year !== 'all' ? year : $t('views.dashboardDetail.summary.totals.all') }}
        </v-chip>
      </v-chip-group>

      <BarLoading v-if="!loadedData" />
      <template v-else>
        <v-row>
          <v-col cols="12" lg="5" class="py-0 pt-4" :class="{ 'pr-4': isLg }">
            <v-simple-table fixed-header>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalProfit.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalProfit.info')"
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'orders')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency
                        :value="summary.totalProfit"
                        :currency="user.baseCurrency"
                        :text-green="summary.totalProfit > 0"
                        :text-red="summary.totalProfit < 0"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{
                        $t('views.dashboardDetail.summary.totals.totalDividendsBaseCurrency.title')
                      }}
                      <InfoIcon
                        :size="15"
                        :text="
                          $t('views.dashboardDetail.summary.totals.totalDividendsBaseCurrency.info')
                        "
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'dividends')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency
                        :value="summary.totalDividendsBaseCurrency"
                        :currency="user.baseCurrency"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalInterest.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalInterest.info')"
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'commissionsInterests')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency :value="summary.totalInterest" :currency="user.baseCurrency" />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalStakeReward.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalStakeReward.info')"
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'dividends')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency :value="summary.totalStakeReward" :currency="user.baseCurrency" />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalBonus.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalBonus.info')"
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'dividends')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency :value="summary.totalBonus" :currency="user.baseCurrency" />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalPaidInterest.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalPaidInterest.info')"
                      />
                    </td>
                    <td class="text-right input-small">
                      <currency
                        :value="summary.totalPaidInterest"
                        :currency="user.baseCurrency"
                        :text-red="summary.totalPaidInterest < 0"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalCommission.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalCommission.info')"
                      />
                      <span class="ml-2">
                        <a @click="$emit('goTab', 'commissionsInterests')">
                          {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                        </a>
                      </span>
                    </td>
                    <td class="text-right input-small">
                      <currency
                        :value="summary.totalCommission"
                        :currency="user.baseCurrency"
                        :text-red="summary.totalCommission < 0"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {{ $t('views.dashboardDetail.summary.totals.totalPlBaseCurrency.title') }}
                      <InfoIcon
                        :size="15"
                        :text="$t('views.dashboardDetail.summary.totals.totalPlBaseCurrency.info')"
                      />
                      <router-link class="ml-2" to="/">
                        {{ $t('views.dashboardDetail.summary.totals.showDetail') }}
                      </router-link>
                    </td>
                    <td class="text-right input-small">
                      <currency
                        :value="summary.totalPlBaseCurrency"
                        :currency="user.baseCurrency"
                        :text-green="summary.totalPlBaseCurrency > 0"
                        :text-red="summary.totalPlBaseCurrency < 0"
                      />
                    </td>
                  </tr>
                  <tr class="row-total">
                    <td>
                      TOTAL
                    </td>
                    <td class="text-right input-small">
                      <currency :value="summary.total" :currency="user.baseCurrency" />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
          <v-col cols="12" lg="7" class="py-0 pt-8" :class="{ 'pl-3': isLg }">
            <BarChart
              v-if="loadedData"
              ref="barChart"
              :chartData="barChartData"
              :chartOptions="barChartOptions"
            />
          </v-col>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { getSummary } from '@/lib/api.js'
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import { getCurrencyValueAsString } from '@/lib/utils/currencies.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import BarLoading from '@/views/components/core/BarLoading.vue'
import Currency from '@/views/components/common/Currency'
import InfoIcon from '@/views/components/common/InfoIcon'
import BarChart from '@/views/components/core/chartjs/BarChart'

export default {
  components: {
    BarLoading,
    Currency,
    InfoIcon,
    BarChart,
  },
  data() {
    return {
      summary: [],
      filters: {},
      loadedData: false,
      positionType: null,
      barChartData: {
        labels: null,
        datasets: null,
      },
      yearSelected: 'all',
      yearsToSelect: ['all'],
    }
  },
  computed: {
    ...mapState(['user', 'hideAmounts']),
    isLg() {
      return this.$vuetify.breakpoint.lgAndUp
    },
    barChartOptions() {
      const context = this

      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
  },
  watch: {
    yearSelected: {
      async handler() {
        await this.loadSummary()
      },
    },
  },
  async mounted() {
    const portfolioAge = await getSummary({
      type: 'portfolio-age',
    })
    this.yearsToSelect = ['all', ...portfolioAge.data]
  },
  methods: {
    createBarChartObject(data) {
      const keys = []
      const values = []
      for (const key of Object.keys(data)) {
        if (key !== 'total' && data[key] !== 0) {
          keys.push(key)
          values.push(data[key])
        }
      }

      this.barChartData.datasets = [
        {
          data: values,
          backgroundColor: values.map((item) =>
            item < 0
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.chartsBarRed
                : DEFAULT_PALETTE.chartsBarRed
              : this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen
          ),
        },
      ]
      this.barChartData.labels = keys.map((elem) =>
        this.$t(`views.dashboardDetail.summary.totals.${elem}.title`)
      )

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    async loadSummary() {
      try {
        let startDate, endDate
        if (this.yearSelected !== 'all') {
          startDate = DateTime.fromObject({ year: this.yearSelected }).startOf('year').toISO()
          endDate = DateTime.fromObject({ year: this.yearSelected }).endOf('year').toISO()
        }

        const positionType =
          this.positionType === 'all'
            ? undefined
            : this.positionType === 'allNeCash'
            ? { $ne: 'cash' }
            : this.positionType
        let summary = await getSummary({
          type: 'totals',
          queryString: {
            startDate,
            endDate,
            filter: {
              type: positionType,
              ...this.filters,
            },
          },
        })
        summary = summary.data
        this.summary = summary
        this.createBarChartObject(summary)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
    },
    async load({ filters, positionType }) {
      this.loadedData = false
      this.filters = filters
      this.positionType = positionType
      await this.loadSummary()
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#summary-totals {
  a {
    text-decoration: none;
  }

  .v-card {
    height: 100%;
  }
}
</style>
